import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { User, Shield, Bell, Palette, LogOut } from 'lucide-react';
import api from '../services/api';

export default function Settings() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '' });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // For now just show a toast - profile update endpoint can be added
      toast.success('Profile settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account and preferences</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <User size={16} />
            Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="settings-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <Button onClick={handleSaveProfile} disabled={saving} className="bg-primary">
            {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </CardContent>
      </Card>

      {/* CRM Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield size={16} />
            System Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              ['CRM Version', '1.0.0'],
              ['Contact Categories', '14'],
              ['Automation Bots', '14'],
              ['AI Provider', 'Claude (Anthropic)'],
              ['Database', 'MongoDB'],
              ['Default Credentials', 'sam@hamptonshospitality.com / hamptons123'],
            ].map(([label, value]) => (
              <div key={label}>
                <div className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</div>
                <div className="text-foreground mt-0.5">{value}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* About */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Palette size={16} />
            About
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Hamptons XP CRM is a custom automation system built for the Hamptons hospitality and real estate industry.
            It manages 14 contact categories, restaurant revenue intelligence, commercial space tracking, postcard production,
            and outreach sequencing — all powered by Claude AI.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            <strong>Default login:</strong> sam@hamptonshospitality.com / hamptons123
          </p>
        </CardContent>
      </Card>

      {/* Sign out */}
      <Button onClick={logout} variant="destructive" className="w-full">
        <LogOut size={16} className="mr-2" /> Sign Out
      </Button>
    </div>
  );
}
